import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useContext } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { TextButton } from '@flipster/react/design-system/text-button';
import BoltIcon from '@flipster/react/icon/generated/BoltIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { SparkAuctionEvent } from '@flipster/utils/segment/events/spark-auction';

import PastAuctionModal from '@web/app/[locale]/(web)/spark/auction/_past-auction/PastAuctionModal';

import { SparkAuctionIdContext, sparkAuctionsBunja } from '../_bunja';
import { RewardImage } from '../_misc/RewardImage';
import { pastAuctionBunja } from './bunja';

export const PastAuctionsHorizontal = () => {
  const { pastAuctionIdsAtom } = useBunja(sparkAuctionsBunja);
  const pastAuctionIds = useAtomValue(pastAuctionIdsAtom);
  if (pastAuctionIds.length < 1) return null;
  return (
    <div>
      <h2 className={css({ textStyle: 'Title/M', mb: 16 })}>
        <FormattedMessage id='Autogen_common.r251126.s_Past_auctions' />
      </h2>
      <ul
        className={flex({
          mx: { base: -16, tablet: 0 },
          px: { base: 16, tablet: 0 },
          overflowX: 'scroll',
          gap: 8,
          flexWrap: { base: 'initial', tablet: 'wrap' },
          justifyContent: { tablet: 'center' },
          '& li': { w: 300, flexShrink: 0 },
        })}
      >
        {pastAuctionIds.map((id) => (
          <li key={id}>
            <SparkAuctionIdContext value={id}>
              <PastAuctionCard />
            </SparkAuctionIdContext>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const PastAuctionsVertical = () => {
  const { pastAuctionIdsAtom } = useBunja(sparkAuctionsBunja);
  const pastAuctionIds = useAtomValue(pastAuctionIdsAtom);
  if (pastAuctionIds.length < 1) return null;
  return (
    <div className={stack({ gap: 8, w: { base: '100%', tablet: 520 } })}>
      <h2 className={css({ textStyle: 'Title/M' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Past_auctions' />
      </h2>
      <ul className={vstack({ gap: 8, '& li': { w: '100%' } })}>
        {pastAuctionIds.map((id) => (
          <li key={id}>
            <SparkAuctionIdContext value={id}>
              <PastAuctionCard />
            </SparkAuctionIdContext>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PastAuctionCard = () => {
  const t = useTranslations();
  const id = useContext(SparkAuctionIdContext);
  const overlay = useOverlay();
  const { imageUrlAtom, rewardAtom, winnerAtom, auctionResultAtom } = useBunja(pastAuctionBunja);
  const imageUrl = useAtomValue(imageUrlAtom);
  const reward = useAtomValue(rewardAtom);
  const winner = useAtomValue(winnerAtom);
  const auctionResult = useAtomValue(auctionResultAtom);

  return (
    <div
      className={stack({
        px: 16,
        py: 24,
        gap: 24,
        borderRadius: 8,
        textStyle: 'Text/M/Regular',
        bg: 'background.default.level-1',
      })}
    >
      <div className={vstack({ gap: 16 })}>
        <div className={css({ pos: 'relative', w: 107, h: 107 })}>
          <RewardImage imageUrl={imageUrl} />
        </div>
        <span className={css({ textStyle: 'Title/S' })}>{reward}</span>
      </div>
      <div
        className={flex({
          py: 12,
          justifyContent: 'space-between',
          borderTop: '1px solid {colors.border.default.level-1}',
          borderBottom: '1px solid {colors.border.default.level-1}',
        })}
      >
        <div className={stack({ gap: 2 })}>
          <span className={css({ textStyle: 'Text/M/Strong' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Winner' />
          </span>
          <span>{winner.nickname}</span>
        </div>
        <div className={hstack({ gap: 4 })}>
          <BoltIcon size={20} />
          <span>{numberFormatter(winner.amount)}</span>
        </div>
      </div>
      <div className={vstack()}>
        <TextButton
          label={t('Autogen_common.r251126.s_View_auction_results')}
          onClick={() => {
            overlay.open(({ isOpen, onClose }) => (
              <SparkAuctionIdContext value={id}>
                <PastAuctionModal isOpen={isOpen} onClose={onClose} />
              </SparkAuctionIdContext>
            ));
            segment.track(SparkAuctionEvent.AuctionResultsClick, auctionResult);
          }}
        />
      </div>
    </div>
  );
};
